/**
 * "Your session will end soon" — the cheap half of work-in-progress protection (see the auth
 * spec's Work-in-progress protection section): warn early enough that a real person can finish
 * what they were doing, rather than silently trying to recover state after a session has already
 * ended, which risks a duplicate submission.
 *
 * Any click, key or scroll before the token actually expires renews it (`useKeepSessionAlive`) — this
 * banner only ever appears once the tab has sat idle long enough that no such request has run,
 * which is exactly the case that used to end in a hard logout with no warning at all.
 *
 * Same shell placement and "not dismissible" reasoning as `MpesaBanner`: it resolves itself
 * within two minutes either way, and a banner somebody can close is a banner that is closed.
 */

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';

import { useKeepSessionAlive, useSessionExpiryWarning } from '@pos/shared';

export function SessionExpiryBanner() {
  // Real clicks and typing renew the session, so this banner only ever reaches someone idle.
  useKeepSessionAlive();
  const { isExpiringSoon, secondsRemaining } = useSessionExpiryWarning();

  if (!isExpiringSoon || secondsRemaining === null) return null;

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
      <Alert severity="warning" variant="filled" sx={{ borderRadius: 2 }}>
        Your session will end in {secondsRemaining}s if this page stays idle. Click anywhere, or
        save what you&rsquo;re working on, to stay signed in.
      </Alert>
    </Box>
  );
}
